import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { UserContext } from './App';
import api from './api';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowDown, FaHandPaper, FaPause, FaCoins, FaShieldAlt, FaClipboardList } from 'react-icons/fa';
import './Bust.css';

const SUITS = ['♠', '♥', '♦', '♣'];
const VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

const getCardValue = (val) => {
    if (['J', 'Q', 'K'].includes(val)) return 10;
    if (val === 'A') return 11;
    return parseInt(val);
};

const calculateScore = (hand) => {
    let score = 0;
    let aces = 0;
    for (let card of hand) {
        score += card.score;
        if (card.value === 'A') aces += 1;
    }
    while (score > 21 && aces > 0) {
        score -= 10;
        aces -= 1;
    }
    return score;
};

const getDisplayScore = (hand, isDealerHidden = false) => {
    if (!hand || hand.length === 0) return 0;
    
    let score = 0;
    let aces = 0;
    for (let i = 0; i < hand.length; i++) {
        if (isDealerHidden && i === 1) break; // Ignore hole card
        let card = hand[i];
        if (card.hidden) continue;
        score += card.score;
        if (card.value === 'A') aces += 1;
    }
    
    while (score > 21 && aces > 0) {
        score -= 10;
        aces -= 1;
    }
    
    if (aces > 0 && score < 21) {
        return `${score - 10}/${score}`;
    }
    return score;
};

// Removed local deck creation as the backend handles it

const BustGame = () => {
    const [bet, setBet] = useState('');
    const [gameId, setGameId] = useState(null);
    const [playerHand, setPlayerHand] = useState([]);
    const [dealerHand, setDealerHand] = useState([]);
    const [gameState, setGameState] = useState('idle'); // idle, playerTurn, dealerTurn, ended
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // win, lose, tie
    const [showOutcome, setShowOutcome] = useState(false);
    const [autoStandPending, setAutoStandPending] = useState(false);
    const [showRules, setShowRules] = useState(false);
    
    // Delayed visual scores to match animation
    const [visualPlayerScore, setVisualPlayerScore] = useState(0);
    const [visualDealerScore, setVisualDealerScore] = useState(0);

    useEffect(() => {
        if (gameState === 'idle') {
            setShowOutcome(false);
            return;
        }
        if (gameState === 'ended') {
            setShowOutcome(true);
            if (messageType === 'win') {
                const sound = new Audio('/win.mp3');
                sound.volume = 0.5;
                sound.play().catch(e => console.log('Audio play blocked:', e));
            } else if (messageType === 'lose') {
                const sound = new Audio('/lose.mp3');
                sound.volume = 0.5;
                sound.play().catch(e => console.log('Audio play blocked:', e));
            }
        } else {
            setShowOutcome(false);
        }
    }, [gameState, messageType]);

    // Auto-stand when player hits exactly 21
    useEffect(() => {
        if (gameState === 'playerTurn' && playerHand.length >= 2 && !autoStandPending) {
            const score = calculateScore(playerHand);
            if (score === 21) {
                setAutoStandPending(true);
                // Wait for card animation to finish, then auto-stand
                const delay = playerHand.length === 2 ? 700 : 400;
                const timer = setTimeout(() => {
                    handleStandRef.current();
                    setAutoStandPending(false);
                }, delay);
                return () => clearTimeout(timer);
            }
        }
    }, [playerHand, gameState]);

    useEffect(() => {
        // Initial deal (2 cards) finishes animating at 900ms. Hits finish at 300ms.
        const delay = playerHand.length === 2 ? 900 : 300;
        const timer = setTimeout(() => setVisualPlayerScore(getDisplayScore(playerHand)), delay);
        return () => clearTimeout(timer);
    }, [playerHand]);

    useEffect(() => {
        // Initial deal (dealer's first visible card) finishes at 600ms.
        // Dealer draws finish at 300ms.
        const delay = dealerHand.length === 2 && gameState === 'playerTurn' ? 600 : 300;
        const timer = setTimeout(() => {
            setVisualDealerScore(getDisplayScore(dealerHand, gameState === 'playerTurn'));
        }, delay);
        return () => clearTimeout(timer);
    }, [dealerHand, gameState]);

    const { user, setUser } = useContext(UserContext);

    const playCardSound = () => {
        const sound = new Audio('/bj.mp3');
        sound.volume = 0.5;
        sound.play().catch(e => console.log('Audio play blocked:', e));
    };

    const playClickSound = () => {
        const sound = new Audio('/bust.mp3');
        sound.volume = 0.5;
        sound.play().catch(e => console.log('Audio play blocked:', e));
    };

    const startNewGame = async () => {
        const betAmount = parseFloat(bet);
        if (isNaN(betAmount) || betAmount < 0.5) {
            if (window._addToast) window._addToast({ type: 'error', title: 'BET ERROR', message: 'Minimum bet is $0.50' });
            return;
        }
        
        if (!user) {
            if (window._addToast) window._addToast({ type: 'error', title: 'AUTH ERROR', message: 'You must be logged in to play' });
            return;
        }

        try {
            playClickSound();
            
            // Instantly clear old scores so they don't ghost during the new deal animation
            setVisualPlayerScore(0);
            setVisualDealerScore(0);

            // Play card sound for each of the 4 initial cards in sequence
            setTimeout(playCardSound, 50); // player 1
            setTimeout(playCardSound, 350); // dealer 1
            setTimeout(playCardSound, 650); // player 2
            setTimeout(playCardSound, 950); // dealer 2

            const res = await api.post('/bust/start', { amount: betAmount });
            setGameId(res.data.gameId);
            setPlayerHand(res.data.playerHand);
            setDealerHand(res.data.dealerHand);
            setGameState(res.data.status);
            if (res.data.status === 'ended') {
                setMessageType(res.data.messageType);
            } else {
                setMessageType('');
            }
            setUser(prev => ({ ...prev, balance: res.data.balance }));
        } catch (err) {
            if (window._addToast) window._addToast({ type: 'error', title: 'ERROR', message: err.response?.data?.error || 'Failed to start game' });
        }
    };

    const handleHit = async () => {
        if (gameState !== 'playerTurn' || !gameId) return;
        
        try {
            playClickSound();
            const res = await api.post('/bust/hit', { gameId });
            playCardSound();
            setPlayerHand(res.data.playerHand);
            if (res.data.status === 'ended') {
                setTimeout(() => {
                    playCardSound();
                    setDealerHand(res.data.dealerHand);
                    setGameState(res.data.status);
                    setMessageType(res.data.messageType);
                    setUser(prev => ({ ...prev, balance: res.data.balance }));
                }, 350);
            } else {
                setDealerHand(res.data.dealerHand);
                setGameState(res.data.status);
                setUser(prev => ({ ...prev, balance: res.data.balance }));
            }
        } catch (err) {
            if (window._addToast) window._addToast({ type: 'error', title: 'ERROR', message: err.response?.data?.error || 'Failed to hit' });
        }
    };

    // Ref to allow auto-stand effect to call the latest handleStand
    const handleStandRef = React.useRef(null);

    const handleStand = async () => {
        if (gameState !== 'playerTurn' || !gameId) return;
        
        playClickSound();
        setGameState('dealerTurn'); // optimistic UI update
        try {
            const res = await api.post('/bust/stand', { gameId });
            
            // To simulate the dealer drawing cards one by one visually, 
            // we can slowly add the returned dealer cards to the state
            const finalDealerHand = res.data.dealerHand;
            const currentCount = dealerHand.length;
            
            let currentVisualHand = [...dealerHand];
            // reveal hidden card
            if (finalDealerHand.length > 1) {
                currentVisualHand[1] = finalDealerHand[1];
                setDealerHand([...currentVisualHand]);
                playCardSound();
                await new Promise(r => setTimeout(r, 600)); // Pause to let hole card finish flipping
            }

            for (let i = currentCount; i < finalDealerHand.length; i++) {
                playCardSound();
                currentVisualHand.push(finalDealerHand[i]);
                setDealerHand([...currentVisualHand]);
                await new Promise(r => setTimeout(r, 600));
            }
            
            // Cards already finished animating during the loop waits above
            setGameState(res.data.status);
            setMessageType(res.data.messageType);
            setUser(prev => ({ ...prev, balance: res.data.balance }));

        } catch (err) {
            setGameState('playerTurn');
            if (window._addToast) window._addToast({ type: 'error', title: 'ERROR', message: err.response?.data?.error || 'Failed to stand' });
        }
    };

    // Keep ref in sync
    handleStandRef.current = handleStand;

    const handleDouble = async () => {
        if (gameState !== 'playerTurn' || !gameId) return;
        
        try {
            playClickSound();
            const res = await api.post('/bust/double', { gameId });
            playCardSound();
            setBet((parseFloat(bet) * 2).toFixed(2));
            setPlayerHand(res.data.playerHand);
            
            // Similar to stand, simulate dealer drawing if dealer played
            if (res.data.status === 'ended' && res.data.messageType !== 'lose') {
                setTimeout(async () => {
                    const finalDealerHand = res.data.dealerHand;
                    let currentVisualHand = [...dealerHand];
                    if (finalDealerHand.length > 1) {
                        currentVisualHand[1] = finalDealerHand[1];
                        setDealerHand([...currentVisualHand]);
                        playCardSound();
                        await new Promise(r => setTimeout(r, 600));
                    } else {
                        setDealerHand([...currentVisualHand]);
                    }
                    setGameState('dealerTurn');
                    
                    for (let i = dealerHand.length; i < finalDealerHand.length; i++) {
                        playCardSound();
                        currentVisualHand.push(finalDealerHand[i]);
                        setDealerHand([...currentVisualHand]);
                        await new Promise(r => setTimeout(r, 600));
                    }

                    // Cards already finished animating during the loop waits above
                    setGameState(res.data.status);
                    setMessageType(res.data.messageType);
                    setUser(prev => ({ ...prev, balance: res.data.balance }));
                }, 350); // Wait for player's double card to land
            } else {
                setTimeout(() => {
                    playCardSound();
                    setDealerHand(res.data.dealerHand);
                    setGameState(res.data.status);
                    if (res.data.status === 'ended') {
                        setMessageType(res.data.messageType);
                    }
                    setUser(prev => ({ ...prev, balance: res.data.balance }));
                }, 350);
            }
            
        } catch (err) {
            if (window._addToast) window._addToast({ type: 'error', title: 'ERROR', message: err.response?.data?.error || 'Failed to double' });
        }
    };

    const renderCard = (card, hidden = false, idx = 0, isDealer = false) => {
        // Only apply dealing delay to the initial first two cards at the very start
        const isInitialDeal = gameState === 'playerTurn' && playerHand.length <= 2;
        let animDelay = 0;
        if (isInitialDeal && idx < 2) {
            if (isDealer) {
                animDelay = idx === 0 ? 0.3 : 0.9;
            } else {
                animDelay = idx === 0 ? 0 : 0.6;
            }
        }
        
        // Placeholder cards ('?') should always be hidden visually
        const isActuallyHidden = hidden || card.value === '?';
        const isRed = !isActuallyHidden && (card.suit === '♥' || card.suit === '♦');
        
        let finalOutcome = messageType;
        if (isDealer) {
            if (messageType === 'win') finalOutcome = 'lose';
            if (messageType === 'lose') finalOutcome = 'win';
        }
        const outcomeClass = (showOutcome && gameState === 'ended' && !isActuallyHidden) ? `outcome-${finalOutcome}` : '';
        
        // Stable key using gameId + position ensures cards don't fly in from the right on reveal
        const cardKey = `${gameId}-${isDealer ? 'dealer' : 'player'}-${idx}`;

        return (
            <motion.div 
                key={cardKey}
                initial={{ opacity: 0, scale: 0.8, x: 300, y: isDealer ? -360 : -560 }}
                animate={{ 
                    opacity: 1, 
                    scale: 1, 
                    x: idx * 30, 
                    y: idx * 15 
                }}
                transition={{ 
                    duration: 0.6, 
                    delay: animDelay, 
                    ease: [0.23, 1, 0.32, 1]
                }}
                className="bust-card-wrapper"
                style={{ zIndex: idx }}
            >
                <motion.div 
                    className="bust-card-inner"
                    initial={{ rotateY: 180 }}
                    animate={{ rotateY: isActuallyHidden ? 180 : 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                    {/* Back Face */}
                    <div className="bust-card bust-card-back">
                        <div className="bust-card-back-logo">
                            <img src="/scrap.webp" alt="" style={{ width: '30px', opacity: 0.3, filter: 'grayscale(1)' }} />
                        </div>
                    </div>

                    {/* Front Face */}
                    <div 
                        className={`bust-card bust-card-front ${isRed ? 'red' : ''} ${outcomeClass}`} 
                    >
                        {!isActuallyHidden && (
                            <>
                                <div className="bust-card-suit-top">{card.value} {card.suit}</div>
                                <div style={{ fontSize: '32px' }}>{card.value}</div>
                                <div className="bust-card-suit-bottom">{card.value} {card.suit}</div>
                            </>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        );
    };

    return (
        <div className="bust-container">
            {showRules && createPortal(
                <div className="bust-rules-overlay" onClick={() => setShowRules(false)}>
                    <div className="bust-rules-modal" onClick={e => e.stopPropagation()}>
                        <div className="bust-rules-header">
                            <h2><FaClipboardList style={{marginRight: '10px'}} /> Game Rules</h2>
                            <button className="close-btn" onClick={() => setShowRules(false)}>&times;</button>
                        </div>
                        <div className="bust-rules-content">
                            <div className="rule-card">
                                <div className="rule-card-title">Objective</div>
                                <div className="rule-card-desc">Beat the dealer's hand without going over 21.</div>
                            </div>
                            
                            <div className="rules-grid">
                                <div className="rule-item">
                                    <div className="rule-label"><FaArrowDown color="#4ade80" /> Hit</div>
                                    <div className="rule-text">Take another card to improve your hand.</div>
                                </div>
                                <div className="rule-item">
                                    <div className="rule-label"><FaHandPaper color="#ef4444" /> Stand</div>
                                    <div className="rule-text">Keep your current hand and end your turn.</div>
                                </div>
                                <div className="rule-item">
                                    <div className="rule-label"><FaCoins color="#ffb703" /> Double</div>
                                    <div className="rule-text">Double your bet and take exactly one more card.</div>
                                </div>
                            </div>

                            <div className="rule-footer">
                                <div className="rule-footer-item">Blackjack pays <span>6 to 5</span></div>
                                <div className="rule-footer-item">Dealer hits on <span>Soft 17</span></div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            <div className="bust-sidebar">
                <div className="bust-label">Bet Amount ~${bet ? parseFloat(bet).toFixed(2) : '0'}</div>
                <div className="bust-input-group">
                    <div className="bust-input-wrapper">
                        <img src="/scrap.webp" alt="scrap" />
                        <input 
                            type="number" 
                            className="bust-input" 
                            value={bet} 
                            onChange={(e) => setBet(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                            disabled={gameState === 'playerTurn' || gameState === 'dealerTurn'}
                        />
                    </div>
                    <div className="bust-controls">
                        <button className="bust-control-btn" onClick={() => { playClickSound(); setBet((parseFloat(bet) / 2).toFixed(2)); }}>1/2</button>
                        <button className="bust-control-btn" onClick={() => { playClickSound(); setBet((parseFloat(bet) * 2).toFixed(2)); }}>2x</button>
                        <button className="bust-control-btn" onClick={() => {
                            playClickSound();
                            if (user && user.balance) {
                                setBet(user.balance.toFixed(2));
                            }
                        }}>MAX</button>
                    </div>
                </div>

                <div className="bust-actions-grid">
                    <button 
                        className="bust-action-btn" 
                        onClick={handleHit}
                        disabled={gameState !== 'playerTurn'}
                    >
                        <FaArrowDown color="#22c55e" /> Hit
                    </button>
                    <button 
                        className="bust-action-btn" 
                        onClick={handleStand}
                        disabled={gameState !== 'playerTurn'}
                    >
                        <FaHandPaper color="#ef4444" /> Stand
                    </button>
                    <button 
                        className="bust-action-btn" 
                        disabled={true} // Local Split logic omitted for brevity
                    >
                        <FaPause color="#ffb703" style={{ transform: 'rotate(90deg)' }} /> Split
                    </button>
                    <button 
                        className="bust-action-btn" 
                        onClick={handleDouble}
                        disabled={gameState !== 'playerTurn' || playerHand.length > 2}
                    >
                        <FaCoins color="#ffb703" /> Double
                    </button>
                </div>

                <button 
                    className="bust-place-bet" 
                    onClick={startNewGame}
                    disabled={gameState === 'playerTurn' || gameState === 'dealerTurn'}
                >
                    Place bet
                </button>

                <div className="bust-bottom-links">
                    <button className="bust-link-btn" onClick={() => setShowRules(true)}>
                        <FaClipboardList /> Game Rules
                    </button>
                </div>
            </div>

            <div className="bust-main">
                <div className="bust-main-content">
                    {gameState === 'idle' && (
                        <>
                            <div className="bust-title-purple">BLACKJACK PAYS 6 TO 5</div>
                            <div className="bust-subtitle-gray">INSURANCE PAYS 2 TO 1</div>
                        </>
                    )}

                    {gameState !== 'idle' && (
                        <div className="bust-game-area">
                            <div className="bust-hand-container">
                                <div style={{ marginBottom: '16px', fontWeight: 900, color: '#9ca3af', letterSpacing: '1.5px', textTransform: 'uppercase', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Dealer
                                </div>
                                {Boolean(visualDealerScore) && visualDealerScore !== 0 && (
                                    <div className="bust-hand-score">{visualDealerScore}</div>
                                )}
                                <div className="bust-cards" style={{ width: 90 + (dealerHand.length - 1) * 30, height: 130 + (dealerHand.length - 1) * 15 }}>
                                    <AnimatePresence>
                                        {dealerHand.map((card, idx) => 
                                            renderCard(card, gameState === 'playerTurn' && idx === 1, idx, true)
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <div className="bust-hand-container">
                                <div style={{ marginBottom: '16px', fontWeight: 900, color: '#fff', letterSpacing: '1.5px', textTransform: 'uppercase', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    You
                                </div>
                                {Boolean(visualPlayerScore) && visualPlayerScore !== 0 && (
                                    <div className="bust-hand-score">{visualPlayerScore}</div>
                                )}
                                <div className="bust-cards" style={{ width: 90 + (playerHand.length - 1) * 30, height: 130 + (playerHand.length - 1) * 15 }}>
                                    <AnimatePresence>
                                        {playerHand.map((card, idx) => renderCard(card, false, idx, false))}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BustGame;

